/** An @-mention being typed: where its "@" is, where the caret is, and the letters between. */
export interface MentionMatch {
  start: number;
  end: number;
  query: string;
}

const MAX_QUERY = 60;

/**
 * The mention the caret is in, if any: an "@" at the start or after whitespace (so an email
 * address never opens it), followed by letters with no whitespace up to the caret.
 */
export function findMention(text: string, caret: number): MentionMatch | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf("@");
  if (at < 0 || (at > 0 && !/\s/.test(before[at - 1]))) return null;
  const query = before.slice(at + 1);
  if (/\s/.test(query) || query.length > MAX_QUERY) return null;
  return { start: at, end: caret, query };
}

/**
 * Names containing the query (any case), in their order, at most `limit`. Given `groupOf`, the
 * groups share the rows (one from each in turn), so a long group does not push the others off.
 */
export function matching<T extends { name: string }>(
  items: T[],
  query: string,
  limit = 8,
  groupOf?: (item: T) => string
): T[] {
  const wanted = query.toLocaleLowerCase();
  const found = items.filter((item) => item.name.toLocaleLowerCase().includes(wanted));
  if (!groupOf) return found.slice(0, limit);
  const groups = new Map<string, T[]>();
  for (const item of found) {
    const key = groupOf(item);
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  }
  const shares = new Map([...groups.keys()].map((key) => [key, 0]));
  let rows = 0;
  for (let round = 0; rows < limit && rows < found.length; round++) {
    for (const [key, group] of groups) {
      if (rows < limit && round < group.length) {
        shares.set(key, round + 1);
        rows++;
      }
    }
  }
  return [...groups].flatMap(([key, group]) => group.slice(0, shares.get(key)));
}

/** The name split around the first match, for bolding it. */
export function splitMatch(name: string, query: string): [string, string, string] {
  const at = query ? name.toLocaleLowerCase().indexOf(query.toLocaleLowerCase()) : -1;
  if (at < 0) return [name, "", ""];
  return [name.slice(0, at), name.slice(at, at + query.length), name.slice(at + query.length)];
}

/**
 * A finished mention in the text: "@" and the name of a file attached to the same draft or
 * message. The name is the whole link: the file it means is that attachment (resolveMention).
 */
export interface MentionToken {
  start: number;
  end: number;
  name: string;
}

/** How a mention of `name` is written in the text. */
export const mentionText = (name: string) => `@${name}`;

const WORD = /[\p{L}\p{N}]/u;

/**
 * The mentions in `text` of the files called `names`: an "@" at the start or after whitespace
 * (as findMention), the longest name that follows it, then the end or a non-letter. Anything
 * else, a typed "@" or a name no attached file has, is plain text.
 */
export function findMentions(text: string, names: Iterable<string>): MentionToken[] {
  const longestFirst = [...new Set(names)].filter(Boolean).sort((a, b) => b.length - a.length);
  if (!longestFirst.length) return [];
  const tokens: MentionToken[] = [];
  for (let at = text.indexOf("@"); at >= 0; at = text.indexOf("@", at + 1)) {
    if (at > 0 && !/\s/.test(text[at - 1])) continue;
    const name = longestFirst.find((candidate) => {
      const end = at + 1 + candidate.length;
      return text.startsWith(candidate, at + 1) && (end === text.length || !WORD.test(text[end]));
    });
    if (!name) continue;
    tokens.push({ start: at, end: at + 1 + name.length, name });
    at += name.length;
  }
  return tokens;
}

/** The file a mention means: the first attached one of that name (two files may share one). */
export function resolveMention<T extends { name: string }>(files: readonly T[], name: string) {
  return files.find((file) => file.name === name);
}

/** The mention `offset` falls strictly inside, if any (its edges are outside it). */
export function mentionAround(tokens: MentionToken[], offset: number): MentionToken | undefined {
  return tokens.find((token) => token.start < offset && offset < token.end);
}

/**
 * A selection that never cuts a mention: a caret inside one goes to the edge it was moving
 * towards (from `previous`), or the nearer one; a range that cuts into one widens over it.
 */
export function snapSelection(
  tokens: MentionToken[],
  start: number,
  end: number,
  previous: number | null
): [number, number] {
  if (start === end) {
    const token = mentionAround(tokens, start);
    if (!token) return [start, end];
    let edge = start - token.start <= token.end - start ? token.start : token.end;
    if (previous != null && previous <= token.start) edge = token.end;
    else if (previous != null && previous >= token.end) edge = token.start;
    return [edge, edge];
  }
  return [mentionAround(tokens, start)?.start ?? start, mentionAround(tokens, end)?.end ?? end];
}

/** The mention a Backspace (caret at its end) or a Delete (caret at its start) removes whole. */
export function mentionToDelete(
  tokens: MentionToken[],
  caret: number,
  key: "Backspace" | "Delete"
): MentionToken | undefined {
  return tokens.find((token) => (key === "Backspace" ? token.end : token.start) === caret);
}

/** `text` with each mention of `name` rewritten by `write`; null when there is none. */
function rewriteMentions(
  text: string,
  names: Iterable<string>,
  name: string,
  write: (token: MentionToken) => [end: number, replacement: string]
): string | null {
  const tokens = findMentions(text, names).filter((token) => token.name === name);
  if (!tokens.length) return null;
  let out = "";
  let from = 0;
  for (const token of tokens) {
    const [end, replacement] = write(token);
    out += text.slice(from, token.start) + replacement;
    from = end;
  }
  return out + text.slice(from);
}

/** `text` without its mentions of `name` (each with the space after it); null if it has none. */
export function stripMentions(text: string, names: Iterable<string>, name: string) {
  return rewriteMentions(text, names, name, (token) => [
    text[token.end] === " " ? token.end + 1 : token.end,
    "",
  ]);
}

/** `text` with its mentions of `from` naming `to` instead; null if it has none. */
export function renameMentions(text: string, names: Iterable<string>, from: string, to: string) {
  return rewriteMentions(text, [...names, from], from, (token) => [token.end, mentionText(to)]);
}

/** The names mentioned in `before` that `after` no longer mentions. */
export function lostMentions(before: string, after: string, names: string[]): string[] {
  const kept = new Set(findMentions(after, names).map((token) => token.name));
  return [...new Set(findMentions(before, names).map((token) => token.name))].filter(
    (name) => !kept.has(name)
  );
}
